<template>
  <div class="page flex-col">
    <AppHeader />

    <!-- Banner（与反无人机产品目录一致） -->
    <div class="sol-banner">
      <div class="sol-banner-mask">
        <span class="sol-banner-zh">{{ isEnglish ? 'Counter-UAS Products' : '反无人机产品' }}</span>
        <span class="sol-banner-en">{{ isEnglish ? 'Anti-drone products' : 'Anti drone products' }}</span>
      </div>
    </div>

    <div class="sol-main">
      <!-- 面包屑 -->
      <nav class="sol-breadcrumb">
        <router-link :to="homeLink">{{ isEnglish ? 'Home' : '首页' }}</router-link>
        <span class="sol-crumb-sep">&gt;</span>
        <span>{{ isEnglish ? 'Counter-UAS Products' : '反无人机产品' }}</span>
        <span class="sol-crumb-sep">&gt;</span>
        <span class="sol-crumb-active">{{ pageTitle }}</span>
      </nav>

      <!-- 页标题 -->
      <div class="sol-page-title-row">
        <h1 class="sol-page-title">{{ pageTitle }}</h1>
        <span class="sol-page-model">{{ productModel }}</span>
      </div>
      <div class="sol-page-line"></div>

      <!-- 产品简介 -->
      <h2 class="sol-h2">{{ isEnglish ? 'Product Overview' : '产品简介' }}</h2>
      <p class="sol-intro">{{ intro }}</p>

      <!-- 核心功能：中心六边形 + 左右三列 -->
      <h2 class="sol-h2">{{ isEnglish ? 'Core Functions' : '核心功能' }}</h2>
      <div class="sol-core">
        <div class="sol-core-col">
          <div
            v-for="(item, index) in featureLeft"
            :key="'fl_' + index"
            class="sol-core-item sol-core-item-left"
          >
            <div class="sol-core-text">
              <div class="sol-core-title">{{ item.title }}</div>
              <p class="sol-core-desc">{{ item.desc }}</p>
            </div>
            <div class="sol-core-icon">
              <img :src="coreIcon" :alt="item.title" />
            </div>
          </div>
        </div>

        <div class="sol-core-center">
          <div class="sol-hex">
            <span v-if="isEnglish">Core<br />Functions</span>
            <span v-else>核心功能</span>
          </div>
        </div>

        <div class="sol-core-col">
          <div
            v-for="(item, index) in featureRight"
            :key="'fr_' + index"
            class="sol-core-item sol-core-item-right"
          >
            <div class="sol-core-icon">
              <img :src="coreIcon" :alt="item.title" />
            </div>
            <div class="sol-core-text">
              <div class="sol-core-title">{{ item.title }}</div>
              <p class="sol-core-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 系统组成：时间轴卡片 -->
      <h2 class="sol-h2">{{ isEnglish ? 'System Composition' : '系统组成' }}</h2>
      <div class="sol-compose">
        <div class="sol-compose-cards">
          <div
            v-for="(item, index) in systemList"
            :key="'sys_' + index"
            class="sol-compose-card"
            :class="{ accent: index === systemList.length - 1 }"
          >
            <div class="sol-compose-head">{{ item.title }}</div>
            <div class="sol-compose-body">
              <ul>
                <li v-for="(line, lIndex) in item.points" :key="'pt_' + index + '_' + lIndex">
                  {{ line }}
                </li>
              </ul>
              <p v-if="item.note" class="sol-compose-note">{{ item.note }}</p>
            </div>
          </div>
        </div>
        <div class="sol-compose-timeline">
          <span
            v-for="(item, index) in systemList"
            :key="'dot_' + index"
            class="sol-compose-dot"
          ></span>
        </div>
      </div>

      <!-- 系统优势 -->
      <h2 class="sol-h2">{{ isEnglish ? 'System Advantages' : '系统优势' }}</h2>
      <div class="sol-adv-grid">
        <div
          v-for="(item, index) in advantageList"
          :key="'adv_' + index"
          class="sol-adv-card"
        >
          <div class="sol-adv-num">{{ index + 1 }}</div>
          <div class="sol-adv-title">{{ item.title }}</div>
          <p class="sol-adv-desc">{{ item.desc }}</p>
        </div>
      </div>

      <!-- 系统架构图 -->
      <h2 class="sol-h2">{{ isEnglish ? 'System Architecture' : '系统架构图' }}</h2>
      <div class="sol-arch">
        <div class="sol-arch-node sol-arch-wide">{{ archTop }}</div>
        <div class="sol-arch-arrow"></div>
        <div class="sol-arch-row">
          <div
            v-for="(item, index) in archSense"
            :key="'as_' + index"
            class="sol-arch-node"
          >
            {{ item }}
          </div>
        </div>
        <div class="sol-arch-arrow"></div>
        <div class="sol-arch-node sol-arch-wide">{{ archMid }}</div>
        <div class="sol-arch-arrow"></div>
        <div class="sol-arch-row">
          <div
            v-for="(item, index) in archAct"
            :key="'aa_' + index"
            class="sol-arch-node"
          >
            {{ item }}
          </div>
        </div>
        <div class="sol-arch-arrow"></div>
        <div class="sol-arch-node sol-arch-wide">{{ archBottom }}</div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import { isEnglishRoute, siteHomeLink } from '@/utils/lang';

const coreIcon = require('./assets/img/FigmaDDSSlicePNG41ccf103f3a4218c0ba3096f5c8198a3.png');

const CONTENT_ZH = {
  pageTitle: '低空安全管理平台',
  productModel: 'HR-C2',
  intro:
    '低空安全管理平台是一套面向低空安全防护的智能指挥管理平台，融合无线电侦测、低空监视雷达、光电跟踪识别、导航诱骗及无线电反制等多种技术，实现对无人机目标的发现、识别、定位、跟踪、预警、处置及全过程管理。平台支持多源数据融合、智能态势分析、设备联动及统一指挥调度，可广泛应用于机场、政府机关、能源设施、边海防、大型活动、智慧园区等低空安全场景。',
  featureLeft: [
    { title: '多源融合感知', desc: '融合雷达、无线电侦测、光电识别等多源信息，实现全天候低空目标监测' },
    { title: '智能目标识别', desc: '自动识别无人机型号、身份信息、飞行轨迹及飞手位置，支持目标分类管理' },
    { title: '态势可视化', desc: '基于GIS电子地图实时显示目标位置、飞行轨迹、设备状态及报警信息' }
  ],
  featureRight: [
    { title: '智能预警', desc: '自动评估目标威胁等级，生成预警信息，并支持多级报警策略' },
    { title: '联动反制', desc: '自动联动干扰设备、导航诱骗设备及侦打一体设备，实现快速处置' },
    { title: '数据管理', desc: '自动生成报警日志、事件记录、统计报表及历史轨迹，实现全过程可追溯' }
  ],
  systemList: [
    {
      title: '无线电侦测系统',
      points: ['无源侦测无人机及飞手信号', '支持主流无人机协议识别', '无人机身份（SN）识别', '飞手定位', '飞行轨迹跟踪']
    },
    {
      title: '雷达探测与光电跟踪',
      points: ['低空监视雷达实现远距离全天候探测', '光电系统自动识别、锁定并连续跟踪目标', '雷达与光电联动，提高目标确认效率']
    },
    {
      title: 'AI智能预警平台',
      points: ['AI智能识别无人机目标及飞行行为', '自动分析飞行轨迹和威胁等级', '多设备联动响应', '支持公安、应急等业务平台对接']
    },
    {
      title: '智能反制系统',
      points: ['支持多种反制方式：', '无线电干扰', '导航诱骗', '定向驱离', '精准迫降', '网捕拦截'],
      note: '根据预设策略自动或人工选择反制方式'
    }
  ],
  advantageList: [
    { title: '极致性价比', desc: '融合雷达、无线电及光电感知，实现全天候、全方位低空监测。' },
    { title: '智能协同', desc: '多设备联动，统一管理，实现目标发现、识别、定位及处置闭环。' },
    { title: '多维反制', desc: '支持干扰、诱骗、网捕等多种反制方式，满足不同场景需求。' },
    { title: '灵活部署', desc: '支持固定式、车载式、便携式及分布式组网部署，适应不同应用环境。' }
  ],
  archTop: '无人机综合管控平台',
  archMid: 'AI智能融合分析',
  archBottom: '驱离｜迫降｜拦截｜取证',
  archSense: ['雷达探测', '无线电侦测', '光电识别'],
  archAct: ['干扰设备', '导航诱骗', '网捕设备']
};

const CONTENT_EN = {
  pageTitle: 'Low-Altitude Security Platform',
  productModel: 'HR-C2',
  intro:
    'The Low-Altitude Security Platform is an intelligent command-and-control system for low-altitude protection. It integrates RF detection, low-altitude surveillance radar, EO tracking/identification, navigation spoofing, and RF countermeasures to discover, identify, locate, track, warn, and respond to UAV targets throughout the full lifecycle. With multi-source data fusion, intelligent situational analysis, device linkage, and unified command, it serves airports, government sites, energy facilities, border/coastal defense, major events, and smart campuses.',
  featureLeft: [
    { title: 'Multi-Source Sensing', desc: 'Fuses radar, RF detection, and EO identification for all-weather low-altitude monitoring.' },
    { title: 'Intelligent Target ID', desc: 'Automatically identifies UAV model, identity, flight path, and operator location with classified management.' },
    { title: 'Situational Visualization', desc: 'GIS-based real-time display of target position, tracks, device status, and alerts.' }
  ],
  featureRight: [
    { title: 'Smart Early Warning', desc: 'Assesses threat levels automatically and supports multi-tier alert policies.' },
    { title: 'Linked Countermeasures', desc: 'Coordinates jamming, spoofing, and integrated detect-and-counter devices for rapid response.' },
    { title: 'Data Management', desc: 'Generates alert logs, event records, reports, and historical tracks for full traceability.' }
  ],
  systemList: [
    {
      title: 'RF Detection System',
      points: ['Passive detection of UAV and operator signals', 'Mainstream UAV protocol recognition', 'UAV identity (SN) identification', 'Operator locating', 'Flight path tracking']
    },
    {
      title: 'Radar Detection & EO Tracking',
      points: ['Long-range all-weather radar detection', 'EO auto-identify, lock, and continuous track', 'Radar–EO cueing for faster confirmation']
    },
    {
      title: 'AI Early-Warning Platform',
      points: ['AI recognition of UAV targets and flight behavior', 'Automatic track and threat analysis', 'Multi-device linked response', 'Integration with public-security / emergency platforms']
    },
    {
      title: 'Intelligent Countermeasure System',
      points: ['Multiple response modes:', 'RF jamming', 'Navigation spoofing', 'Directional drive-away', 'Precision force-landing', 'Net capture'],
      note: 'Automatically or manually select countermeasures based on preset policies'
    }
  ],
  advantageList: [
    { title: 'Cost-Effective Coverage', desc: 'Combines radar, RF, and EO sensing for all-weather, all-around low-altitude monitoring.' },
    { title: 'Intelligent Coordination', desc: 'Unified multi-device management closes the loop from discovery to response.' },
    { title: 'Multi-Dimensional Response', desc: 'Supports jamming, spoofing, net capture, and more for diverse scenarios.' },
    { title: 'Flexible Deployment', desc: 'Fixed, vehicle-mounted, portable, and distributed networking for different environments.' }
  ],
  archTop: 'Integrated UAV Command Platform',
  archMid: 'AI Fusion Analysis',
  archBottom: 'Drive-away | Force-land | Intercept | Evidence',
  archSense: ['Radar Detection', 'RF Detection', 'EO Identification'],
  archAct: ['Jamming Devices', 'Navigation Spoofing', 'Net Capture']
};

export default {
  name: 'SolutionLowAltitudePlatform',
  data() {
    return { coreIcon };
  },
  computed: {
    isEnglish() {
      return isEnglishRoute(this.$route);
    },
    homeLink() {
      return siteHomeLink(this.$route);
    },
    content() {
      return this.isEnglish ? CONTENT_EN : CONTENT_ZH;
    },
    pageTitle() {
      return this.content.pageTitle;
    },
    productModel() {
      return this.content.productModel;
    },
    intro() {
      return this.content.intro;
    },
    featureLeft() {
      return this.content.featureLeft;
    },
    featureRight() {
      return this.content.featureRight;
    },
    systemList() {
      return this.content.systemList;
    },
    advantageList() {
      return this.content.advantageList;
    },
    archTop() {
      return this.content.archTop;
    },
    archMid() {
      return this.content.archMid;
    },
    archBottom() {
      return this.content.archBottom;
    },
    archSense() {
      return this.content.archSense;
    },
    archAct() {
      return this.content.archAct;
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Banner（与 lanhu_7chanpinliebiao 产品目录一致） */
.sol-banner {
  width: 100%;
  height: 360px;
  margin-top: 8px;
  background: url(../lanhu_7chanpinliebiao/assets/img/FigmaDDSSlicePNG4bfbddd95358d0de5c2a6890b148e575.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.sol-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
}

.sol-banner-mask {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.sol-banner-zh {
  color: #fff;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.sol-banner-en {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 500;
}

/* 版心 */
.sol-main {
  width: 1200px;
  max-width: 92%;
  margin: 0 auto;
  padding: 36px 0 80px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sol-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 28px;
}

.sol-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.sol-breadcrumb a:hover {
  color: #1e40af;
}

.sol-crumb-sep {
  color: #9ca3af;
}

.sol-crumb-active {
  color: #1e40af;
  font-weight: 600;
}

.sol-page-title-row {
  display: flex;
  align-items: baseline;
  gap: 16px;
  width: 100%;
}

.sol-page-title {
  margin: 0;
  font-size: 28px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-page-model {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 2px;
  color: #94a3b8;
}

.sol-page-line {
  width: 64px;
  height: 3px;
  background: #1e40af;
  margin: 14px 0 32px;
}

.sol-h2 {
  margin: 0 0 18px;
  font-size: 22px;
  font-weight: 800;
  color: #2563eb;
  line-height: 1.3;
  width: 100%;
  text-align: left;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-intro {
  margin: 0 0 44px;
  font-size: 15px;
  line-height: 1.9;
  color: #6b7280;
  text-align: justify;
}

/* ========== 核心功能 ========== */
.sol-core {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 220px 1fr;
  gap: 28px;
  align-items: center;
  margin-bottom: 52px;
  min-height: 360px;
}

.sol-core-col {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.sol-core-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.sol-core-item-left {
  justify-content: flex-end;
  text-align: right;
}

.sol-core-item-right {
  justify-content: flex-start;
  text-align: left;
}

.sol-core-text {
  max-width: 280px;
}

.sol-core-title {
  font-size: 16px;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 6px;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-core-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #6b7280;
}

.sol-core-icon {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #dbeafe;
  border: 2px solid #93c5fd;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.sol-core-icon img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.sol-core-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sol-hex {
  width: 168px;
  height: 194px;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background: linear-gradient(160deg, #60a5fa 0%, #2563eb 55%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 28px rgba(37, 99, 235, 0.28);
}

.sol-hex span {
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.5px;
  line-height: 1.3;
  text-align: center;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: normal;
  max-width: 88%;
  padding: 0 6px;
  box-sizing: border-box;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

/* ========== 系统组成 ========== */
.sol-compose {
  width: 100%;
  margin-bottom: 52px;
}

.sol-compose-cards {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.sol-compose-card {
  background: #fff;
  border: 1px solid #dbe3f5;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(37, 99, 235, 0.06);
  min-height: 260px;
  display: flex;
  flex-direction: column;
}

.sol-compose-head {
  background: #3b82f6;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  padding: 12px 10px;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-compose-card.accent .sol-compose-head {
  background: #1e40af;
}

.sol-compose-body {
  padding: 16px 14px 18px;
  flex: 1;
}

.sol-compose-body ul {
  margin: 0;
  padding-left: 1.1em;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.85;
}

.sol-compose-note {
  margin: 10px 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.6;
}

.sol-compose-timeline {
  position: relative;
  margin-top: 22px;
  height: 18px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
}

.sol-compose-timeline::before {
  content: '';
  position: absolute;
  left: 12.5%;
  right: 12.5%;
  top: 50%;
  height: 2px;
  background: #93c5fd;
  transform: translateY(-50%);
}

.sol-compose-dot {
  position: relative;
  z-index: 1;
  width: 14px;
  height: 14px;
  margin: 0 auto;
  border-radius: 50%;
  background: #2563eb;
  border: 3px solid #dbeafe;
  box-sizing: border-box;
}

/* ========== 系统优势 ========== */
.sol-adv-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 52px;
}

.sol-adv-card {
  position: relative;
  background: #fff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  padding: 28px 18px 22px;
  box-sizing: border-box;
  min-height: 168px;
}

.sol-adv-num {
  position: absolute;
  top: 0;
  left: 0;
  width: 28px;
  height: 28px;
  background: #2563eb;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sol-adv-title {
  font-size: 16px;
  font-weight: 700;
  color: #2563eb;
  margin: 8px 0 12px;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-adv-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.75;
  color: #6b7280;
}

/* ========== 系统架构图 ========== */
.sol-arch {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding: 8px 0 12px;
  box-sizing: border-box;
}

.sol-arch-row {
  width: 100%;
  max-width: 920px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.sol-arch-node {
  min-height: 52px;
  padding: 14px 18px;
  border-radius: 4px;
  background: #bfdbfe;
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  box-sizing: border-box;
}

.sol-arch-wide {
  width: min(560px, 100%);
  background: #93c5fd;
  color: #1e3a8a;
}

.sol-arch-arrow {
  width: 0;
  height: 0;
  margin: 10px 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 12px solid #60a5fa;
}

@media (max-width: 1100px) {
  .sol-core {
    grid-template-columns: 1fr;
    gap: 24px;
    min-height: auto;
  }

  .sol-core-center {
    order: -1;
  }

  .sol-core-item-left,
  .sol-core-item-right {
    justify-content: flex-start;
    text-align: left;
  }

  .sol-core-item-left {
    flex-direction: row-reverse;
  }

  .sol-compose-cards,
  .sol-adv-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .sol-banner {
    height: 240px;
  }

  .sol-banner-zh {
    font-size: 30px;
  }

  .sol-page-title {
    font-size: 24px;
  }

  .sol-compose-cards,
  .sol-adv-grid,
  .sol-arch-row {
    grid-template-columns: 1fr;
  }

  .sol-compose-timeline {
    display: none;
  }
}
</style>
